// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// What the browser says to a player who is wearing a headset (M9-47, 06 §7, NFR-022).
//
// **The browser had no way to say anything to a presenting player at all.** Every sentence it
// produces in an immersive session goes to the DOM: `XRFrameReport.toast` is read once, in
// `MachineStage.tsx`, and rendered as HTML — and in an immersive WebXR session the DOM is not
// composited, so none of it is on screen. That is the seated-mode offer, the recycle countdown,
// the room-fit note, "look where you want the machine and pinch", the anchor-lost line, and the
// input-scheme change M9-38 finally gave a reader. It is also **NFR-022's second half**: *"every
// spoken callout is captioned on screen **and in the headset**"*, and 06 §6 names the surface —
// *"a screen-fixed panel in the headset"*. The native client has drawn one since M7-22. The
// browser has drawn nothing.
//
// **The same panel the native client draws, to the dot.** `App/Xr/SessionNotice.cs` is 256 × 32
// dots of the machine's own 5 × 7 font, two centred lines, transparent where there is no dot, and
// this is its twin — same size, same rows, same break rules, held to it by
// `core/fixtures/parity/session-notice-v1.json`. Two clients that laid the same sentence out
// differently would be two answers to a question a player only ever asks once: *what did it just
// say?*
//
// **Why a dot-matrix panel and not a nice one.** The native file's reasoning carries exactly:
// this is the only text the product ever puts in front of a player mid-ball, and a system font
// floating in the air reads as the operating system interrupting them. A DMD line reads as the
// machine talking. It also costs nothing that is not already here — the font, the palette and the
// raster are the backglass display's.
//
// Pure. It composes a raster; `src/world/noticePanel.ts` is what hangs one in the room.

import { DmdRaster, dmdPalette, drawText, measureText } from '../fx/dmd'

/**
 * Panel size in dots. Powers of two, deliberately.
 *
 * A backglass at 1024 × 768 came out of a Quest as a black slab because 768 is not a power of two
 * and a tile-based GPU will not build that mip chain. This panel is small enough that nobody would
 * have thought about it, which is exactly the circumstance that produced the black slab — and the
 * browser runs on the same tile-based GPUs in the same headset.
 */
export const NOTICE_WIDTH = 256
export const NOTICE_HEIGHT = 32

/** Where the two lines sit, top of glyph. Seven dots tall each, with a line between. */
export const NOTICE_FIRST_LINE_TOP = 5
export const NOTICE_SECOND_LINE_TOP = 18

/**
 * How long a one-frame sentence stays up, seconds.
 *
 * A toast is raised for a single frame — `XRRuntime` clears `this.toast` at the top of every
 * `advance` — so a panel that showed exactly what it was handed would flash for 14 ms and be
 * gone. Four seconds is long enough to read eleven words at a glance a player can spare mid-ball
 * and short enough that it is off the screen before it becomes furniture.
 *
 * A caption is **not** held this way: it comes with its own dwell from the audio layer, which
 * knows how long the line it captions lasts. Holding it here as well would leave the last thing
 * the machine said hanging over the next thing.
 */
export const NOTICE_TOAST_SECONDS = 4

export interface NoticeLines {
  readonly first: string
  /** Empty when the sentence fits on one row. */
  readonly second: string
}

/**
 * Split a sentence into the two rows the panel draws.
 *
 * **An author's `" - "` wins over the automatic wrap**, because a sentence with two halves knows
 * where its own break belongs. `recycleWarning`'s two sentences carry one, which is why they were
 * the only strings this panel had ever been asked to draw and why the wrap below went unwritten on
 * this side for two milestones.
 */
export function noticeLines(text: string): NoticeLines {
  const line = text ?? ''
  const split = line.indexOf(' - ')
  if (split >= 0) return { first: line.slice(0, split), second: line.slice(split + 3) }
  return wrapNotice(line)
}

/**
 * Break a line too wide for the panel onto the second row, at a space.
 *
 * The break is the **last** space that still fits, so the first row is as full as it can be and the
 * wrap reads as a wrap rather than as two short lines.
 *
 * **A word that cannot fit on its own is left alone deliberately.** Nothing sensible can be done
 * with a 43-character unbroken token on a 42-glyph row: breaking mid-word invents a hyphen the
 * author did not write, and `noticeLeft` already clamps to the left edge so what is drawn starts at
 * the beginning of the word rather than in its middle. The overflow is visible, which is the honest
 * failure for text nobody can shorten.
 *
 * **This is the case that matters now.** 11 §5 lets an author type any caption they like, and
 * NFR-022 puts those captions on this panel — so the guarantee the native file's header calls "fits
 * by construction" expired the moment the second caller landed. NEON NOVA's longest callout is 22
 * glyphs of 42, which is what makes the wrap a safety net rather than something the flagship
 * depends on.
 */
export function wrapNotice(line: string): NoticeLines {
  if (line.length === 0 || measureText(line) <= NOTICE_WIDTH) return { first: line, second: '' }

  let breakAt = -1
  for (let i = 0; i < line.length; i++) {
    if (line[i] !== ' ') continue
    if (measureText(line.slice(0, i)) > NOTICE_WIDTH) break
    breakAt = i
  }
  if (breakAt < 0) return { first: line, second: '' }
  return { first: line.slice(0, breakAt), second: line.slice(breakAt + 1) }
}

/** Left edge that centres a line, never negative — a line too long starts at the edge. */
export function noticeLeft(line: string): number {
  const wide = measureText(line)
  const left = (NOTICE_WIDTH - wide) >> 1
  return left < 0 ? 0 : left
}

/** The panel as dots, which is what a test reads and what the texture is built from. */
export function noticeRaster(text: string): DmdRaster {
  const raster = new DmdRaster(NOTICE_WIDTH, NOTICE_HEIGHT)
  const { first, second } = noticeLines(text)
  if (first.length > 0) drawText(raster, first, noticeLeft(first), NOTICE_FIRST_LINE_TOP)
  if (second.length > 0) drawText(raster, second, noticeLeft(second), NOTICE_SECOND_LINE_TOP)
  return raster
}

/**
 * Dots to pixels, in the display's own amber.
 *
 * **A dot's level drives the alpha as well as the brightness**, so a half-lit dot is a dimmer dot
 * rather than a grey one — and, more to the point, an unlit dot is *nothing at all*. A black
 * rectangle hanging in a passthrough room is a hole cut in the room; lit dots on nothing is a sign.
 */
export function noticePixels(text: string): Uint8ClampedArray<ArrayBuffer> {
  const raster = noticeRaster(text)
  const [r, g, b] = dmdPalette('amber')
  // **A plain `ArrayBuffer`, spelt out.** `ImageData` will not take a `Uint8ClampedArray` over an
  // `ArrayBufferLike`, because that union includes `SharedArrayBuffer` and an `ImageData` may not
  // be built on shared memory. The allocation here is already a plain buffer; the annotation is
  // what says so to the checker rather than a cast that says "trust me".
  const pixels = new Uint8ClampedArray(new ArrayBuffer(NOTICE_WIDTH * NOTICE_HEIGHT * 4))
  for (let i = 0; i < raster.dots.length; i++) {
    const level = raster.dots[i]! / 15
    pixels[i * 4] = r * level
    pixels[i * 4 + 1] = g * level
    pixels[i * 4 + 2] = b * level
    pixels[i * 4 + 3] = 255 * level
  }
  return pixels
}
